<!-- eslint-disable vue/no-parsing-error -->
<template>
  <div>
    <div
      v-if="isLoading === true"
      class="text-center mb-2"
    >
      <b-spinner
        variant="primary"
        label="Text Centered"
      />
    </div>

    <b-tabs
      v-model="activeTab"
      content-class="mt-2"
      nav-class="delivery-notes-tabs"
      @input="onTabChange"
    >
      <b-tab
        :title="tabTitleToPrint"
        active
      >
        <template #title>
          <feather-icon
            icon="PrinterIcon"
            class="mr-50"
          />
          <span>À imprimer</span>
          <b-badge
            v-if="unprintedCount > 0"
            variant="primary"
            pill
            class="ml-50"
          >
            {{ unprintedCount }}
          </b-badge>
        </template>
      </b-tab>
      <b-tab :title="tabTitlePrinted">
        <template #title>
          <feather-icon
            icon="CheckCircleIcon"
            class="mr-50"
          />
          <span>Déjà imprimés</span>
          <b-badge
            v-if="printedCount > 0"
            variant="light-secondary"
            pill
            class="ml-50"
          >
            {{ printedCount }}
          </b-badge>
        </template>
      </b-tab>
    </b-tabs>

    <div class="custom-search d-flex justify-content-between mt-1">
      <label
        class="mr-1 h4"
        style="color:black"
      >Nombre de bons de livraison : {{ orders.length }}</label>

      <div class="custom-search d-flex justify-content-end">
        <b-form-group>
          <div class="d-flex align-items-center">
            <label class="mr-1">Recherche de bon de livraison</label>
            <b-form-input
              v-model="searchTerm"
              placeholder="Rechercher par N° BL, ID externe, client, téléphone..."
              type="text"
              class="d-inline-block"
            />
          </div>
        </b-form-group>
        <b-form-group>
          <b-button
            v-if="user.role === 'supplier'"
            :to="{ name: 'order-add' }"
            variant="gradient-primary"
            class="ml-1 d-inline-block"
          >
            <feather-icon icon="PlusIcon" />
            <span class="ml-1">Ajouter un bon de livraison</span>
          </b-button>
        </b-form-group>
        <b-form-group>
          <b-button
            variant="gradient-primary"
            class="ml-1 d-inline-block"
            :class="{ 'disabled-cursor': selectedOrders.selectedRows.length === 0 }"
            :disabled="selectedOrders.selectedRows.length === 0"
            @click="printAll"
          >
            <feather-icon icon="PrinterIcon" />
            <span class="ml-1">{{ activeTab === 0 ? 'Impression groupée' : 'Réimprimer' }}</span>
          </b-button>
        </b-form-group>
      </div>
    </div>

    <div
      v-if="orders.length === 0 && !isLoading"
      class="empty-state"
    >
      <div class="empty-icon">
        <feather-icon
          :icon="activeTab === 0 ? 'PrinterIcon' : 'CheckCircleIcon'"
          size="80"
        />
      </div>
      <div class="empty-title">
        {{ activeTab === 0 ? 'Aucun bon à imprimer' : 'Aucun bon déjà imprimé' }}
      </div>
      <div class="empty-description">
        <span v-if="activeTab === 0">
          <span v-if="user.role === 'supplier'">
            Vous n'avez pas de bons de livraison à imprimer.<br>
            Cliquez sur le bouton ci-dessous pour en ajouter un nouveau.
          </span>
          <span v-else>
            Aucun bon de livraison n'est en attente d'impression.
          </span>
        </span>
        <span v-else>
          Vous n'avez pas encore imprimé de bons de livraison.<br>
          Imprimez vos premiers bons depuis l'onglet "À imprimer".
        </span>
      </div>
      <div v-if="user.role === 'supplier' && activeTab === 0">
        <b-button
          :to="{ name: 'order-add' }"
          variant="gradient-primary"
          class="empty-action mt-2"
        >
          <feather-icon icon="PlusIcon" />
          <span class="ml-1">Créer un bon de livraison</span>
        </b-button>
      </div>
    </div>
    <vue-good-table
      v-else
      ref="my-table"
      :columns="columns"
      :rows="orders"
      :search-options="{
        enabled: true,
        externalQuery: searchTerm,
      }"
      :select-options="{
        enabled: true,
        selectOnCheckboxOnly: true,
        selectionInfoClass: 'custom-class',
        selectionText: 'bons sélectionnés',
        clearSelectionText: 'Tout désélectionner',
        disableSelectInfo: false,
      }"
      @on-selected-rows-change="selectionChanged"
    >
      <template
        slot="table-row"
        slot-scope="props"
      >
        <template v-if="props.column.isOrderReference">
          <span class="text-nowrap font-weight-bold">
            {{ props.row.display_cross_tenant_barcode && props.row.cross_tenant_barcode ? props.row.cross_tenant_barcode : props.row.id }}
          </span>
        </template>

        <template v-else-if="props.column.field === 'status'">
          <div v-if="props.row.status === 0">
            <b-badge
              class="badge-glow bg-warning bg-lighten-1"
              pill
            >
              <feather-icon
                icon="XIcon"
                class="mr-25"
              />
              <span> en attente</span>
            </b-badge>
          </div>
          <div v-else-if="props.row.status === 1">
            <b-badge
              class="badge-glow bg-warning bg-darken-2"
              pill
            >
              <feather-icon
                icon="XIcon"
                class="mr-25"
              />
              <span> en pickup </span>
            </b-badge>
          </div>
          <div v-else-if="props.row.status === 15">
            <b-badge
              class="badge-glow bg-info"
              pill
            >
              <feather-icon
                icon="ClockIcon"
                class="mr-25"
              />
              <span> en attente de collecte </span>
            </b-badge>
          </div>
        </template>

        <template v-else-if="props.column.field === 'printed_at'">
          <div v-if="props.row.printed_at">
            <b-badge
              variant="light-success"
              pill
              class="d-flex align-items-center"
              style="width: fit-content;"
            >
              <feather-icon
                icon="CheckIcon"
                class="mr-25"
              />
              <span>{{ formatPrintedDate(props.row.printed_at) }}</span>
            </b-badge>
          </div>
          <div v-else>
            <b-badge
              variant="light-warning"
              pill
            >
              <feather-icon
                icon="PrinterIcon"
                class="mr-25"
              />
              <span>Non imprimé</span>
            </b-badge>
          </div>
        </template>

        <template v-else>
          {{ props.formattedRow[props.column.field] }}
        </template>
      </template>

      <template
        slot="pagination-bottom"
        slot-scope="props"
      >
        <div class="d-flex justify-content-between flex-wrap">
          <div class="d-flex align-items-center mb-0 mt-1">
            <span class="text-nowrap"> Afficher 1 à </span>
            <b-form-select
              v-model="pageLength"
              :options="['3', '5', '10']"
              class="mx-1"
              @input="
                (value) => props.perPageChanged({ currentPerPage: value })
              "
            />
            <span class="text-nowrap">
              sur {{ props.total }} bons de livraison
            </span>
          </div>
          <div>
            <b-pagination
              :value="1"
              :total-rows="props.total"
              :per-page="pageLength"
              first-number
              last-number
              align="right"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
              @input="(value) => props.pageChanged({ currentPage: value })"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>
          </div>
        </div>
      </template>
    </vue-good-table>
  </div>
</template>

<script>
import {
  BPagination,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BButton,
  BBadge,
  BTabs,
  BTab,
  BSpinner,
} from 'bootstrap-vue'
import { VueGoodTable } from 'vue-good-table'
import axios from 'axios'
import { orderReferenceSearchValue } from '@/utils/orderReference'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import storeAuth from '@/store/store'

export default {
  components: {
    VueGoodTable,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BButton,
    BBadge,
    BTabs,
    BTab,
    BSpinner,
  },
  data() {
    return {
      pageLength: 3,
      dir: false,
      columns: [],
      user: {},
      orders: [],
      searchTerm: '',
      isLoading: false,
      selectedOrders: {
        selectedRows: [],
      },
      activeTab: 0,
      unprintedCount: 0,
      printedCount: 0,
      allDeliveryNotes: null,
    }
  },
  computed: {
    tabTitleToPrint() {
      return `À imprimer (${this.unprintedCount})`
    },
    tabTitlePrinted() {
      return `Déjà imprimés (${this.printedCount})`
    },
  },
  created() {
    this.user = storeAuth.state.user
    this.columns = this.getColumns()
    this.getOrders()
    this.getCounts()
  },
  methods: {
    onTabChange(tabIndex) {
      this.activeTab = tabIndex
      this.getOrders()
      this.selectedOrders = { selectedRows: [] }
    },
    printAll() {
      const selected = this.$refs['my-table'].selectedRows
      if (selected.length === 0) {
        this.showToast('danger', 'top-center', 'vous devez sélectionner au minimum un colis')
      } else {
        const orderIds = selected.map(x => x.id).join(',')
        this.$router.push({ name: 'print-all', params: { orderIds } })
      }
    },
    formatFn(date) {
      if (!date) {
        return null
      }
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      const h = date.slice(11, 19)
      return `${year}-${month.padStart(2, '0')}-${day1.padStart(
        2,
        '0',
      )} | ${h}`
    },
    formatPrintedDate(date) {
      if (!date) {
        return null
      }
      const d = new Date(date)
      const day = String(d.getDate()).padStart(2, '0')
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const hours = String(d.getHours()).padStart(2, '0')
      const minutes = String(d.getMinutes()).padStart(2, '0')
      return `${day}/${month} ${hours}:${minutes}`
    },
    async getOrders() {
      try {
        this.isLoading = true
        if (this.allDeliveryNotes === null) {
          const { data } = await axios.get('/api/orders/get-delivery-notes/?include_printed=true')
          this.allDeliveryNotes = data
          this.updateCounts()
        }

        this.applyActiveTabFilter()
        this.isLoading = false
      } catch (error) {
        this.isLoading = false
        setTimeout(() => {
          this.showToast('danger', 'top-center', error.toString())
        }, 1000)
      }
    },
    applyActiveTabFilter() {
      const includePrinted = this.activeTab === 1
      this.orders = (this.allDeliveryNotes || []).filter(order => (
        includePrinted ? order.printed_at !== null : order.printed_at === null
      ))
    },
    updateCounts() {
      const deliveryNotes = this.allDeliveryNotes || []
      this.unprintedCount = deliveryNotes.filter(order => order.printed_at === null).length
      this.printedCount = deliveryNotes.filter(order => order.printed_at !== null).length
    },
    selectionChanged(selectedRows) {
      this.selectedOrders = selectedRows
    },
    showToast(variant, position, text) {
      this.$toast(
        {
          component: ToastificationContent,
          props: {
            title: 'Notification',
            icon: 'InfoIcon',
            text,
            variant,
          },
        },
        {
          position,
        },
      )
    },
    getColumns() {
      const baseColumns = [
        {
          label: 'N° BL',
          field: orderReferenceSearchValue,
          isOrderReference: true,
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher BL / ID externe',
          },
        },
        {
          label: 'Destinataire',
          field: 'name',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher destinataire',
          },
        },
      ]

      if (this.user.role !== 'supplier') {
        baseColumns.push({
          label: 'Expéditeur',
          field: 'supplier',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher expéditeur',
          },
        })
      }

      const remainingColumns = [
        {
          label: 'Gouvernorat',
          field: 'governorate',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher gouvernorat',
          },
        },
        {
          label: 'Délégation',
          field: 'delegation',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher délégation',
          },
        },
        {
          label: 'Téléphone',
          field: 'phone1',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher téléphone',
          },
        },
        {
          label: 'Statut',
          field: 'status',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher statut',
          },
        },
        {
          label: 'Montant TTC',
          field: 'price_ttc',
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher montant TTC',
          },
        },
        {
          label: 'Date de création',
          field: 'created_at',
          formatFn: this.formatFn,
          filterOptions: {
            enabled: true,
            placeholder: 'Chercher date de création',
          },
        },
        {
          label: 'Impression',
          field: 'printed_at',
          sortable: true,
          filterOptions: {
            enabled: false,
          },
        },
      ]

      return [...baseColumns, ...remainingColumns]
    },
  },
}
</script>
<style lang="scss">
@import "@core/scss/vue/libs/vue-good-table.scss";

.delivery-notes-tabs {
  .nav-link {
    display: flex;
    align-items: center;
    font-weight: 500;

    &.active {
      font-weight: 600;
    }
  }
}

.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  margin-bottom: 2rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}
.empty-icon {
  width: 80px;
  height: 80px;
  margin: 0 auto 2rem;
  background: rgba(10, 38, 52, 0.1);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0A2634;
}
.empty-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 1rem;
  letter-spacing: -0.025em;
}
.empty-description {
  color: #6b7280;
  margin-bottom: 2rem;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
  font-size: 1.1rem;
  line-height: 1.6;
}
.empty-action {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #0A2634 0%, #c70000 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1rem;
  box-shadow: 0 4px 12px rgba(10, 38, 52, 0.3);
}
.empty-action:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(10, 38, 52, 0.4);
  color: white;
  text-decoration: none;
}
</style>
